import clsx from "clsx";
import { useMemo, useState } from "react";
import { OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import {
  Check,
  Exclamation,
  ExclamationTriangleFill,
  TrashFill,
} from "react-bootstrap-icons";
import { Link } from "react-router-dom";
import { useInterval } from "usehooks-ts";
import { AlertType, DocumentStatus, DocumentType } from "@/briefpoint-client";
import Button from "@/components/Button";
import ConfirmDeleteDocumentModal from "@/components/ConfirmDeleteDocumentModal/";
import Help from "@/components/Help";
import type { DocumentClient } from "@/lib/types";
import { formatLongDate } from "@/lib/utils";
import styles from "./ProcessingBox.module.scss";

interface Props {
  document: DocumentClient;
  uploading?: boolean;
}

export default function ProcessingBox({ document, uploading }: Props) {
  const uploadedDateTime = formatLongDate(document.uploadDate);
  const filename = document.name;
  const error = document.status === DocumentStatus.Error;
  const fixInProgress = document.alert === AlertType.FixInProgress;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const processingProgress = (document.info?.parsingProgress ?? 0) * 100;

  const showComplete = useMemo(() => {
    return (
      document.status >= DocumentStatus.Processed || processingProgress >= 100
    );
  }, [document.status, processingProgress]);

  const containerClassName = fixInProgress
    ? fixInProgress
    : error
      ? "error"
      : showComplete
        ? "complete"
        : "processing";
  let processingText = "";
  if (!error && processingProgress > 0 && processingProgress <= 39) {
    processingText = "Identifying document...";
  } else if (!error && processingProgress > 39 && processingProgress <= 99) {
    processingText = `Extracting ${document.info?.documentType === DocumentType.Pleading ? "case data" : "requests"}...`;
  }

  const headingPrefix = uploading
    ? "Uploading"
    : fixInProgress
      ? "Fix in progress for"
      : error
        ? "Error processing document"
        : showComplete
          ? "Processing complete:"
          : "Currently processing";

  // Only hide "Processing complete:" on tablet - other prefixes like
  // "Error processing document" or "Fix in progress for" should always be visible
  const showProcessingCompletePrefix = showComplete && !error && !fixInProgress;

  return (
    <>
      <ConfirmDeleteDocumentModal
        showDeleteConfirm={showDeleteConfirm}
        setShowDeleteConfirm={setShowDeleteConfirm}
        document={document}
      />
      <div
        id={`processing-box-${document.id}`}
        className={clsx(styles.processingBox, {
          [styles.complete]: containerClassName === "complete",
          [styles.error]: containerClassName === "error",
        })}
      >
        <div className={styles.details}>
          {fixInProgress ? (
            <div className={styles.fixInProgress}>
              <ExclamationTriangleFill />
            </div>
          ) : error ? (
            <div className={styles.errorIcon}>
              <Exclamation />
            </div>
          ) : showComplete ? (
            <div className={styles.completeIcon}>
              <Check />
            </div>
          ) : (
            <Spinner
              style={{ width: "1.3rem", height: "1.3rem" }}
              animation="border"
              variant="success"
            >
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          )}
          <div className={styles.details}>
            <div className={styles.detailsInner}>
              <small className={styles.headingText}>
                {/* Hide prefix on tablet only for "Processing complete:" state */}
                {showProcessingCompletePrefix ? (
                  <span className={styles.hideOnTablet}>{headingPrefix} </span>
                ) : (
                  <>{headingPrefix} </>
                )}
                <b className={styles.filename}>{filename}.</b>
              </small>
              {processingText && (
                <small className={styles.detailsInnerSmall}>
                  {processingText}
                </small>
              )}
              {fixInProgress ? (
                <small className={styles.headerTwoWrap}>
                  Please check back later.
                </small>
              ) : null}
              {error && (
                <Help
                  id={`${document.id}-error`}
                  text="Briefpoint encountered an issue processing your document and will investigate further, you can also let us know by using the Feedback button on the right."
                />
              )}
              {/* This text should be smaller than small  */}
              {uploadedDateTime && showComplete && (
                <small className={styles.detailsInnerSmall}>
                  <span className={styles.hideOnTablet}>Uploaded </span>
                  {uploadedDateTime}.
                </small>
              )}
            </div>
          </div>
          {showComplete && !error && (
            <div className={styles.startedLinkWrapper}>
              <Button
                variant="outline"
                size="sm"
                className="wide btn-hover-bg-fix"
                to={`/wizard/${document.id}/source-document-review`}
              >
                Get Started
              </Button>
            </div>
          )}
          <div className={styles.deleteButtonContainer}>
            <OverlayTrigger overlay={<Tooltip>Delete Permanently</Tooltip>}>
              <TrashFill
                onClick={() => setShowDeleteConfirm(true)}
                className={clsx("cursor-pointer", styles.deleteButton)}
                title="Delete"
              />
            </OverlayTrigger>
          </div>
        </div>
        {!showComplete && !error && (
          <div
            className={styles.loadingBar}
            style={{ width: `${processingProgress}%` }}
          />
        )}
      </div>
    </>
  );
}
